import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { X, Copy, Check } from 'lucide-react';
import { useJob, useCancelJob, useRequeueJob } from '../../../hooks/useJobs';
import { formatRelativeTime } from '../../../utils/formatters';
import { useToast } from '../../../contexts/ToastContext';

function JobDetailDrawer({ jobId, onClose }) {
  const toast = useToast();
  const [copiedId, setCopiedId] = useState(false);
  const [payloadExpanded, setPayloadExpanded] = useState(false);
  const previewIframeRef = useRef(null);

  const { data, isLoading } = useJob(jobId);
  const cancelMutation = useCancelJob();
  const requeueMutation = useRequeueJob();

  const job = data?.data;

  // Close on Escape key
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const handleCopyId = async () => {
    if (!job) return;
    try {
      await navigator.clipboard.writeText(job.id);
      setCopiedId(true);
      setTimeout(() => setCopiedId(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleCancel = async () => {
    if (!window.confirm('Cancel this job?')) return;

    try {
      await cancelMutation.mutateAsync(jobId);
      toast.success('Job canceled');
      onClose();
    } catch (err) {
      toast.error(err.message || 'Failed to cancel job');
    }
  };

  const handleRequeue = async () => {
    if (!window.confirm('Requeue this job?')) return;

    try {
      await requeueMutation.mutateAsync(jobId);
      toast.success('Job requeued');
      onClose();
    } catch (err) {
      toast.error(err.message || 'Failed to requeue job');
    }
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'queued':
        return 'queue-status-queued';
      case 'processing':
        return 'queue-status-processing';
      case 'sent':
        return 'queue-status-sent';
      case 'failed':
        return 'queue-status-failed';
      case 'canceled':
        return 'queue-status-canceled';
      default:
        return '';
    }
  };

  return (
    <>
      {/* Overlay */}
      <div className="drawer-overlay" onClick={onClose} />

      {/* Drawer Panel */}
      <div className="drawer-panel">
        {isLoading ? (
          <div className="drawer-loading">
            <div className="skeleton-row" style={{ height: '60px' }} />
            <div className="skeleton-row" style={{ height: '200px', marginTop: '20px' }} />
            <div className="skeleton-row" style={{ height: '150px', marginTop: '20px' }} />
          </div>
        ) : !job ? (
          <div className="drawer-error">Job not found</div>
        ) : (
          <>
            {/* Header */}
            <div className="drawer-header">
              <div className="drawer-header-content">
                <div className="drawer-job-id">
                  <span>{job.id.slice(0, 8)}</span>
                  <button className="drawer-copy-btn" onClick={handleCopyId} title="Copy full ID">
                    {copiedId ? <Check size={14} /> : <Copy size={14} />}
                  </button>
                </div>
                <span className={`queue-status-badge ${getStatusBadgeClass(job.status)}`}>
                  {job.status}
                </span>
              </div>
              <button className="drawer-close-btn" onClick={onClose}>
                <X size={20} />
              </button>
            </div>

            {/* Body */}
            <div className="drawer-body">
              {/* Section 1: Overview */}
              <div className="drawer-section">
                <h4 className="drawer-section-title">Overview</h4>
                <div className="drawer-field">
                  <span className="drawer-field-label">Recipient</span>
                  <span className="drawer-field-value">
                    {job.payload_snapshot?.customer?.email || 'Unknown'}
                  </span>
                </div>
                <div className="drawer-field">
                  <span className="drawer-field-label">Template</span>
                  <span className="drawer-field-value">
                    {job.template_name ? (
                      <Link to={`/admin/email/templates/${job.template_id}`}>
                        {job.template_name}
                      </Link>
                    ) : (
                      '(deleted)'
                    )}
                  </span>
                </div>
                <div className="drawer-field">
                  <span className="drawer-field-label">Rule</span>
                  <span className="drawer-field-value">
                    {job.rule_id ? (
                      <Link to={`/admin/email/rules/${job.rule_id}`}>{job.rule_name}</Link>
                    ) : (
                      'Manual Send'
                    )}
                  </span>
                </div>
                {job.reservation_id && (
                  <div className="drawer-field">
                    <span className="drawer-field-label">Reservation ID</span>
                    <span className="drawer-field-value">{job.reservation_id}</span>
                  </div>
                )}
                <div className="drawer-field">
                  <span className="drawer-field-label">Created</span>
                  <span className="drawer-field-value">
                    {new Date(job.created_at).toLocaleString()} (
                    {formatRelativeTime(job.created_at)})
                  </span>
                </div>
                <div className="drawer-field">
                  <span className="drawer-field-label">Send At</span>
                  <span className="drawer-field-value">
                    {new Date(job.send_at).toLocaleString()} ({formatRelativeTime(job.send_at)})
                  </span>
                </div>
                {job.dedupe_key && (
                  <div className="drawer-field">
                    <span className="drawer-field-label">Dedupe Key</span>
                    <span className="drawer-field-value drawer-field-mono">{job.dedupe_key}</span>
                  </div>
                )}
              </div>

              {/* Section 2: Email Preview */}
              {job.rendered_body && (
                <div className="drawer-section">
                  <h4 className="drawer-section-title">Email Preview</h4>
                  {job.rendered_subject && (
                    <div className="drawer-field">
                      <span className="drawer-field-label">Subject</span>
                      <span className="drawer-field-value">{job.rendered_subject}</span>
                    </div>
                  )}
                  <div className="drawer-preview-frame-wrapper">
                    <iframe
                      ref={previewIframeRef}
                      title="Email Preview"
                      className="drawer-preview-frame"
                      sandbox=""
                      srcDoc={job.rendered_body}
                      onLoad={() => {
                        const iframe = previewIframeRef.current;
                        if (iframe && iframe.contentDocument) {
                          iframe.style.height =
                            iframe.contentDocument.documentElement.scrollHeight + 'px';
                        }
                      }}
                    />
                  </div>
                </div>
              )}

              {/* Section 3: Status History */}
              {(job.status === 'failed' || job.status === 'sent') && (
                <div className="drawer-section">
                  <h4 className="drawer-section-title">Status History</h4>
                  {job.status === 'failed' && (
                    <div className="drawer-error-box">
                      <div className="drawer-error-title">
                        Error (Attempt {job.attempts} of {job.max_attempts})
                      </div>
                      <div className="drawer-error-message">{job.last_error}</div>
                    </div>
                  )}
                  {job.status === 'sent' && job.logs && job.logs.length > 0 && (
                    <div className="drawer-field">
                      <span className="drawer-field-label">Sent</span>
                      <span className="drawer-field-value">
                        {new Date(job.logs[0].sent_at || job.logs[0].created_at).toLocaleString()}
                      </span>
                    </div>
                  )}
                </div>
              )}

              {/* Section 3: Delivery Log */}
              <div className="drawer-section">
                <h4 className="drawer-section-title">Delivery Log</h4>
                {!job.logs || job.logs.length === 0 ? (
                  <p className="drawer-empty-text">No delivery attempts yet</p>
                ) : (
                  <table className="drawer-log-table">
                    <thead>
                      <tr>
                        <th>Timestamp</th>
                        <th>Status</th>
                        <th>Provider ID</th>
                        <th>Error</th>
                      </tr>
                    </thead>
                    <tbody>
                      {job.logs.map((log) => (
                        <tr key={log.id}>
                          <td>{new Date(log.created_at).toLocaleString()}</td>
                          <td>
                            <span className={`drawer-log-status drawer-log-status-${log.status}`}>
                              {log.status}
                            </span>
                          </td>
                          <td className="drawer-log-mono">
                            {log.provider_message_id || '—'}
                          </td>
                          <td className="drawer-log-error">{log.error || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>

              {/* Section 4: Payload */}
              <div className="drawer-section">
                <h4
                  className="drawer-section-title drawer-section-toggle"
                  onClick={() => setPayloadExpanded(!payloadExpanded)}
                >
                  Payload Snapshot {payloadExpanded ? '▼' : '▶'}
                </h4>
                {payloadExpanded && (
                  <pre className="drawer-payload">
                    {JSON.stringify(job.payload_snapshot, null, 2)}
                  </pre>
                )}
              </div>
            </div>

            {/* Actions */}
            <div className="drawer-actions">
              {job.status === 'queued' && (
                <button
                  className="btn btn-danger"
                  onClick={handleCancel}
                  disabled={cancelMutation.isPending}
                >
                  Cancel Job
                </button>
              )}
              {(job.status === 'failed' || job.status === 'canceled') && (
                <button
                  className="btn btn-primary"
                  onClick={handleRequeue}
                  disabled={requeueMutation.isPending}
                >
                  Requeue Job
                </button>
              )}
              <button className="btn btn-secondary" onClick={onClose}>
                Close
              </button>
            </div>
          </>
        )}
      </div>
    </>
  );
}

export default JobDetailDrawer;
